"use client";

import { ensureMsalReady } from "@/lib/auth/client";
import {
    buildCanonicalOAuthSignInUrl,
    rememberPostAuthOrigin,
    shouldDelegateOAuthToCanonicalHost,
    urlHasOAuthRedirectResponse,
} from "@/lib/auth/entra/redirect-uri";

const SCOPES = ["openid", "profile", "email"];

async function persistIdTokenToSession(idToken: string): Promise<boolean> {
    const res = await fetch("/api/auth/entra/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ idToken }),
    });
    return res.ok;
}

/**
 * Ensure the httpOnly session cookie holds a non-expired Entra id_token.
 * Returns false when MSAL has no account (user must sign in interactively).
 */
export async function ensureEntraServerSession(): Promise<boolean> {
    try {
        const me = await fetch("/api/auth/me", { credentials: "include" });
        if (me.ok) return true;
    } catch {
        /* fall through to silent refresh */
    }

    const msal = await ensureMsalReady();
    if (!msal) return false;

    const accounts = msal.getAllAccounts();
    if (accounts.length === 0) return false;

    try {
        const result = await msal.acquireTokenSilent({
            scopes: SCOPES,
            account: accounts[0],
        });
        if (!result?.idToken) return false;
        return persistIdTokenToSession(result.idToken);
    } catch {
        return false;
    }
}

export async function entraLoginRedirect(): Promise<void> {
    if (typeof window !== "undefined") {
        const origin = window.location.origin;

        if (shouldDelegateOAuthToCanonicalHost(origin)) {
            rememberPostAuthOrigin(origin);
            window.location.assign(buildCanonicalOAuthSignInUrl(origin));
            return;
        }
    }

    const msal = await ensureMsalReady();
    if (!msal) throw new Error("Entra sign-in is not configured on this deployment.");
    await msal.loginRedirect({ scopes: SCOPES });
}

export type EntraCallbackResult =
    | { ok: true; tenantHomeUrl?: string | null }
    | { ok: false; error: string; code?: string };

let redirectCallbackInFlight: Promise<EntraCallbackResult> | null = null;

export async function entraHandleRedirectCallback(): Promise<EntraCallbackResult> {
    if (redirectCallbackInFlight) return redirectCallbackInFlight;

    redirectCallbackInFlight = (async (): Promise<EntraCallbackResult> => {
        const msal = await ensureMsalReady();
        if (!msal) {
            return { ok: false, error: "Entra sign-in is not configured on this deployment." };
        }

        const search = typeof window !== "undefined" ? window.location.search : "";
        const hash = typeof window !== "undefined" ? window.location.hash : "";
        const hasOAuthResponse = urlHasOAuthRedirectResponse(search, hash);

        // MSAL v5: navigateToLoginRequestUrl defaults true — would redirect back to
        // /auth/signin before we POST the id_token to our session route.
        const result = await msal.handleRedirectPromise({ navigateToLoginRequestUrl: false });

        if (!result?.idToken) {
            // Invite redemption (or a stale revisit) lands on /auth/callback with no
            // OAuth payload — start interactive sign-in instead of showing a dead-end error.
            if (!hasOAuthResponse) {
                return {
                    ok: false,
                    error: "Complete Microsoft sign-in to open your workspace.",
                    code: "NEEDS_INTERACTIVE_SIGNIN",
                };
            }
            return { ok: false, error: "Microsoft did not return a sign-in token." };
        }

        const res = await fetch("/api/auth/entra/session", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ idToken: result.idToken }),
        });
        const json = (await res.json().catch(() => ({}))) as {
            error?: string;
            code?: string;
            tenantHomeUrl?: string | null;
        };
        if (!res.ok) {
            return {
                ok: false,
                error: json.error || "Sign-in could not be completed.",
                code: json.code,
            };
        }
        return { ok: true, tenantHomeUrl: json.tenantHomeUrl ?? null };
    })();

    try {
        return await redirectCallbackInFlight;
    } finally {
        redirectCallbackInFlight = null;
    }
}

export async function entraSignOut(): Promise<void> {
    try {
        await fetch("/api/auth/entra/session", { method: "DELETE", credentials: "include" });
    } catch {
        /* best-effort */
    }
    const msal = await ensureMsalReady();
    if (msal) {
        await msal.logoutRedirect();
    }
}
